import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '../../store/hooks';
import useLogout from '../../features/auth/hooks/useLogout';
import { getAuthenticatedUsername } from '../../features/auth/utils/jwtPermissions';
import { storage } from '../../utils/storage';
import './Header.css';
import logo from '../../assets/images/white_horizontal_logo.png';
import usFlag from '../../assets/images/flags/us.svg';
import vnFlag from '../../assets/images/flags/vn.svg';

const Header: React.FC = () => {
  const { t, i18n } = useTranslation();
  const { totalItems } = useAppSelector((state) => state.cart.cart);
  const { isAuthenticated } = useAppSelector((state) => state.auth);
  const { isLoggingOut, signOut } = useLogout();
  const [language, setLanguage] = useState(i18n.language === 'vi' ? 'VI' : 'EN');
  const [showLanguageDropdown, setShowLanguageDropdown] = useState(false);
  const [showAccountDropdown, setShowAccountDropdown] = useState(false);
  const accountMenuRef = useRef<HTMLDivElement>(null);
  const username = getAuthenticatedUsername(storage.getAccessToken());

  useEffect(() => {
    if (!showAccountDropdown) return undefined;

    const closeOnOutsideClick = (event: MouseEvent) => {
      if (!accountMenuRef.current?.contains(event.target as Node)) {
        setShowAccountDropdown(false);
      }
    };
    const closeOnEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') setShowAccountDropdown(false);
    };

    document.addEventListener('mousedown', closeOnOutsideClick);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnOutsideClick);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [showAccountDropdown]);

  const changeLanguage = (lang: 'en' | 'vi') => {
    i18n.changeLanguage(lang);
    setLanguage(lang === 'vi' ? 'VI' : 'EN');
    setShowLanguageDropdown(false);
  };

  const toggleLanguageDropdown = () => {
    setShowLanguageDropdown(!showLanguageDropdown);
  };

  return (
    <header className="header">
      <div className="header-topbar">
        <div className="container">
          <div className="topbar-left">
            <a href="tel:0907713959" className="topbar-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20.01 15.38c-1.23 0-2.42-.2-3.53-.56-.35-.12-.74-.03-1.01.24l-1.57 1.97c-2.83-1.35-5.48-3.9-6.89-6.83l1.95-1.66c.27-.28.35-.67.24-1.02-.37-1.11-.56-2.3-.56-3.53 0-.54-.45-.99-.99-.99H4.19C3.65 3 3 3.24 3 3.99 3 13.28 10.73 21 20.01 21c.71 0 .99-.63.99-1.18v-3.45c0-.54-.45-.99-.99-.99z"/>
              </svg>
              {t('header.hotline')}: 0907713959
            </a>
            <span className="topbar-separator">-</span>
            <a href="mailto:trankhang0990@gmail.com" className="topbar-item">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M20 4H4c-1.1 0-2 .9-2 2v12c0 1.1.9 2 2 2h16c1.1 0 2-.9 2-2V6c0-1.1-.9-2-2-2zm0 4l-8 5-8-5V6l8 5 8-5v2z"/>
              </svg>
              {t('header.email')}: trankhang0990@gmail.com
            </a>
          </div>
          <div className="topbar-right">
            <Link to="/about" className="topbar-link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
              </svg>
              {t('header.about')}
            </Link>
            <span className="topbar-separator">|</span>
            <Link to="/help" className="topbar-link">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 17h-2v-2h2v2zm2.07-7.75l-.9.92C13.45 12.9 13 13.5 13 15h-2v-.5c0-1.1.45-2.1 1.17-2.83l1.24-1.26c.37-.36.59-.86.59-1.41 0-1.1-.9-2-2-2s-2 .9-2 2H8c0-2.21 1.79-4 4-4s4 1.79 4 4c0 .88-.36 1.68-.93 2.25z"/>
              </svg>
              {t('header.help')}
            </Link>
          </div>
        </div>
      </div>

      <div className="header-main">
        <div className="container">
          <Link to="/" className="header-logo">
            <img src={logo} alt="Phương Đông Store" />
          </Link>

          <div className="search-bar">
            <input 
              type="text" 
              placeholder={t('header.search_placeholder')}
            />
            <button className="search-btn" type="button" aria-label={t('header.search_placeholder')}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none">
                <path d="M21 21L16.65 16.65M19 11C19 15.4183 15.4183 19 11 19C6.58172 19 3 15.4183 3 11C3 6.58172 6.58172 3 11 3C15.4183 3 19 6.58172 19 11Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round"/>
              </svg>
            </button>
          </div>

          <div className="header-actions">
            <Link
              to="/track-order"
              className="header-action warranty-action"
              aria-label={t('header.track_warranty')}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M9 7h6M9 11h6M6 21h12a2 2 0 002-2V5a2 2 0 00-2-2H6a2 2 0 00-2 2v14a2 2 0 002 2z" stroke="currentColor" fill="none" strokeWidth="2"/>
              </svg>
              <div className="action-text">
                <span className="action-label">{t('header.track_warranty')}</span>
              </div>
            </Link>

            <Link
              to="/admin"
              className="header-action admin-action"
              aria-label={t('header.store_system')}
              title={t('header.store_system')}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z"/>
              </svg>
              <div className="action-text">
                <span className="action-label">{t('header.store_system')}</span>
              </div>
            </Link>

            <Link
              to="/cart"
              className="header-action cart-action"
              aria-label={t('header.cart')}
            >
              <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor">
                <path d="M7 4h14l-1.5 9h-11L7 4zm0 0L5.5 2M7 13h10.5M7 13L5.5 2m9 11l1 8M9 21h6"/>
                <path d="M7 18c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2zm10 0c-1.1 0-2 .9-2 2s.9 2 2 2 2-.9 2-2-.9-2-2-2z" stroke="currentColor" fill="none" strokeWidth="2"/>
              </svg>
              <div className="action-text">
                <span className="action-label">{t('header.cart')}</span>
              </div>
              {totalItems > 0 && <span className="cart-badge">{totalItems}</span>}
            </Link>

            <div className="language-dropdown">
              <button
                onClick={toggleLanguageDropdown}
                className="language-toggle"
                type="button"
                aria-expanded={showLanguageDropdown}
              >
                <img 
                  src={language === 'EN' ? usFlag : vnFlag} 
                  alt={language} 
                  className="current-flag"
                />
                <span>{language}</span>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="currentColor" className="dropdown-arrow">
                  <path d="M7 10l5 5 5-5z"/>
                </svg>
              </button>
              {showLanguageDropdown && (
                <div className="language-menu">
                  <button 
                    className={`language-option ${language === 'EN' ? 'active' : ''}`}
                    onClick={() => changeLanguage('en')}
                  >
                    <img src={usFlag} alt="US Flag" className="flag-icon" />
                    <span>English</span>
                  </button>
                  <button 
                    className={`language-option ${language === 'VI' ? 'active' : ''}`}
                    onClick={() => changeLanguage('vi')}
                  >
                    <img src={vnFlag} alt="Vietnam Flag" className="flag-icon" />
                    <span>Tiếng Việt</span>
                  </button>
                </div>
              )}
            </div>

            {isAuthenticated ? (
              <div className="account-dropdown" ref={accountMenuRef}>
                <button
                  type="button"
                  className="account-toggle"
                  aria-expanded={showAccountDropdown}
                  aria-haspopup="menu"
                  onClick={() => {
                    setShowLanguageDropdown(false);
                    setShowAccountDropdown((isOpen) => !isOpen);
                  }}
                >
                  <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                  </svg>
                  <span>{username ?? t('header.account')}</span>
                  <svg className="account-toggle-arrow" width="12" height="12" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                    <path d="M7 10l5 5 5-5z" />
                  </svg>
                </button>

                {showAccountDropdown && (
                  <div className="account-menu" role="menu">
                    <Link
                      to="/account"
                      className="account-menu-item"
                      role="menuitem"
                      onClick={() => setShowAccountDropdown(false)}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">person</span>
                      {t('header.account_information')}
                    </Link>
                    <button
                      type="button"
                      className="account-menu-item account-menu-logout"
                      role="menuitem"
                      disabled={isLoggingOut}
                      onClick={() => {
                        setShowAccountDropdown(false);
                        void signOut();
                      }}
                    >
                      <span className="material-symbols-outlined" aria-hidden="true">logout</span>
                      {isLoggingOut ? t('header.logging_out') : t('header.logout')}
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <Link to="/login" className="auth-link">
                <svg width="24" height="24" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
                  <path d="M12 12c2.21 0 4-1.79 4-4s-1.79-4-4-4-4 1.79-4 4 1.79 4 4 4zm0 2c-2.67 0-8 1.34-8 4v2h16v-2c0-2.66-5.33-4-8-4z" />
                </svg>
                <span>{t('header.login')}</span>
              </Link>
            )}
          </div>
        </div>
      </div>

      <nav className="header-nav">
        <div className="container">
          <ul className="nav-menu">
            <li className="nav-item">
              <Link to="/products?category=iphone" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M17.25 8.5c-.1-2.5-2.1-4.5-4.6-4.6-.3 0-.6-.4-.6-.9s.3-1 .6-1c3.4.1 6.2 2.9 6.4 6.4 0 .3-.4.6-.9.6s-.9-.2-.9-.5zm-2.9-.1c0-.3-.3-.6-.6-.6-1.1-.1-2-.9-2.1-2-.1-.3-.3-.6-.6-.6s-.6.3-.6.6c.1 1.9 1.6 3.4 3.5 3.5.3.1.5-.2.5-.5s-.1-.3-.1-.4z"/>
                  <rect x="7" y="4" width="10" height="16" rx="2" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                </svg>
                <span>{t('nav.iphone')}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/products?category=ipad" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="4" y="3" width="16" height="18" rx="2" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                  <circle cx="12" cy="18" r="1"/>
                </svg>
                <span>{t('nav.ipad')}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/products?category=watch" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="7" y="7" width="10" height="10" rx="2" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                  <path d="M9 4h6v3H9zM9 17h6v3H9z" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                </svg>
                <span>{t('nav.watch')}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/products?category=mac" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <rect x="3" y="4" width="18" height="12" rx="1" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                  <path d="M8 16h8l1 4H7l1-4z" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                  <line x1="6" y1="20" x2="18" y2="20" stroke="currentColor" strokeWidth="1.5"/>
                </svg>
                <span>{t('nav.mac')}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/products?category=audio" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 2a7 7 0 00-7 7v3a3 3 0 003 3h1V9a6 6 0 0112 0v6h1a3 3 0 003-3V9a7 7 0 00-7-7z" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                  <rect x="5" y="12" width="4" height="8" rx="2" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                  <rect x="15" y="12" width="4" height="8" rx="2" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                </svg>
                <span>{t('nav.audio')}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/products?category=accessory" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M13 10V3L4 14h7v7l9-11h-7z" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                </svg>
                <span>{t('nav.accessories')}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/products?category=old" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                </svg>
                <span>{t('nav.installment')}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/thu-cu" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M4 7h16M10 11v6m4-6v6M5 7l1 12a2 2 0 002 2h8a2 2 0 002-2l1-12M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                </svg>
                <span>{t('nav.trade_in')}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/promotion" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M7 7h10l-5.5 6L7 7zm5.5 6L7 20h10l-4.5-7z" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                </svg>
                <span>{t('nav.sale')}</span>
              </Link>
            </li>
            <li className="nav-item">
              <Link to="/news" className="nav-link">
                <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                  <path d="M19 20H5a2 2 0 01-2-2V6a2 2 0 012-2h10a2 2 0 012 2v1m2 13a2 2 0 01-2-2V7m2 13a2 2 0 002-2V9.5a2.5 2.5 0 00-2.5-2.5H15" stroke="currentColor" fill="none" strokeWidth="1.5"/>
                </svg>
                <span>{t('nav.news')}</span>
              </Link>
            </li>
          </ul>
        </div>
      </nav>
    </header>
  );
};

export default Header;
